import AddressSchema from "_shared/addresses/address-schema";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { MarketplaceResultType } from "config";
import CompanyProfileSchema from "marketplace/companies/company-profile-schema";
import IndividualSchema from "marketplace/individuals/_schema/individual-schema";
import { z } from "zod";

export type MarketplaceSearchInfoJSON = z.infer<typeof MarketplaceSearchResponseSchema.SearchInfoJSONZ>;

export default class MarketplaceSearchResponseSchema {
  // Relaxed individual schema for nested search result fields where the API
  // may return required string fields (firstName, lastName, legalEntityId) as null.
  public static readonly SearchNestedIndividualJSONZ = IndividualSchema.IndividualJSONZ.extend({
    legalEntityId: z.uuid().nullish(),
    isVisible: z.boolean().nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
  });

   public static readonly SearchInfoJSONZ = z.object({
    id: z.uuid(),
    type: z.enum(MarketplaceResultType),
    isVisible: z.boolean().nullish(),
    // Individual
    userId: z.uuid().nullish(),
    legalEntityId: z.uuid().nullish(),
    companyId: z.uuid().nullish(),
    companyName: z.string().nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    description: z.string().nullish(),
    location: AddressSchema.AddressJSONZ.nullish(),
    phoneNumber: z.string().nullish(),
    avatar: z.uuid().nullish(),
    email: z.email().nullish(),
    school: z.string().nullish(),
    degree: z.string().nullish(),
    resume: z.string().nullish(),
    professionalRegistration: z.string().nullish(),
    technicalBackground: z.string().nullish(),
    numberOfApplicationsDraftedProsecuted: z.number().nullish(),
    numberOfAllowedApplications: z.number().nullish(),
    barAdmissionYear: z.number().nullish(),
    isOnCall: z.boolean().nullish(),
    insuredAmount: z.number().nullish(),
    billingRate: z.string().nullish(),
    potentialDiversityCandidate: z.boolean().nullish(),
    collaborates: z.boolean().nullish(),
    additionalInfo: z.string().nullish(),
    links: z.array(z.string()).nullish(),
    professionalPublications: z.array(z.string()).nullish(),
    sampleApplications: z.array(z.string()).nullish(),
    fluentLanguages: z.array(z.string()).nullish(),
    technicalLanguages: z.array(z.string()).nullish(),
    regionsRegistered: z.array(z.string()).nullish(),
    isVendorRep: z.boolean().nullish(),
    // Company
    name: z.string().nullish(),
    yearEstablished: z.number().nullish(),
    numberOfEmployees: z.number().nullish(),
    website: z.string().nullish(),
    categories: z.array(z.string()).nullish(),
    contact: MarketplaceSearchResponseSchema.SearchNestedIndividualJSONZ.nullish(),
    // Team
    leader: MarketplaceSearchResponseSchema.SearchNestedIndividualJSONZ.nullish(),
    members: z.array(MarketplaceSearchResponseSchema.SearchNestedIndividualJSONZ).nullish(),
    totalMembers: z.number().nullish(),
    associatedFirms: z.array(CompanyProfileSchema.CompanyProfileJSONZ).nullish(),
    totalFirms: z.number().nullish(),
  });

  public static readonly HitDataZ = z
    .object({
      ...MarketplaceSearchResponseSchema.SearchInfoJSONZ.shape,
    })
    .loose();

  public static readonly ResponseJSONZ = z
    .object({
      hits: z
        .array(
          z.object({
            data: MarketplaceSearchResponseSchema.HitDataZ,
            score: z.number().nullish(),
            highlight: z.record(z.string(), z.array(z.string())).nullish(),
          }),
        )
        .nullish(),
      from: z.number().nullish(),
      size: z.number().nullish(),
      total: z.number().nullish(),
    })
    .loose();

  public static readonly InfoZ = z
    .object({
      id: z.uuid(),
      type: z.enum(MarketplaceResultType),
    })
    .loose();

  public static validateResponseJSON(json: unknown): z.infer<typeof MarketplaceSearchResponseSchema.ResponseJSONZ> {
    return parseOrThrow(MarketplaceSearchResponseSchema.ResponseJSONZ, json, {
      logError: true,
      schemaName: "MarketplaceSearchResponseSchema",
      operation: "validateResponseJSON",
      payloadSample: json,
    });
  }

  public static validateJSON(json: unknown): MarketplaceSearchInfoJSON {
    return parseOrThrow(MarketplaceSearchResponseSchema.SearchInfoJSONZ, json, {
      logError: true,
      schemaName: "MarketplaceSearchResponseSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }
}
